import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X, FileText, Users, Mail, Phone, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const emptyPost = { title: "", excerpt: "", content: "", category: "Marketing", cover_image: "", author: "Digital Madhuraa", published: true };

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("blog");
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState({ contacts: [], consultations: [], subscribers: [] });
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const authCfg = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (user === false) navigate("/admin");
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([
        api.get("/blog"),
        api.get("/admin/leads", authCfg()),
      ]);
      setPosts(p.data);
      setLeads(l.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [authCfg]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const openNew = () => { setForm(emptyPost); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/blog", form, authCfg());
        toast.success("Post published");
      } else {
        await api.put(`/blog/${editing.id}`, form, authCfg());
        toast.success("Post updated");
      }
      setEditing(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/blog/${id}`, authCfg());
      toast.success("Post deleted");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin"); };

  if (!user) return <main className="min-h-screen grid place-items-center text-slate-400">Loading…</main>;

  return (
    <main className="min-h-screen bg-[var(--dm-alt)]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.jpeg" alt="Digital Madhuraa" className="w-8 h-8 rounded-lg object-cover" style={{ objectPosition: "center 40%" }} />
            <span className="font-head font-extrabold text-slate-900">Admin Console</span>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-head font-bold text-slate-700 hover:border-red-300 hover:text-red-600 transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-8">
          {[["blog", "Blog Posts", FileText], ["leads", "Leads & Subscribers", Users]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-head font-bold transition-colors ${tab === k ? "text-white" : "bg-white text-slate-700 border border-slate-200"}`} style={tab === k ? { background: "linear-gradient(135deg,#6C2BD9,#2563EB)" } : {}}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "blog" && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-head font-extrabold text-xl text-slate-900">{posts.length} Posts</h2>
              <button onClick={openNew} data-testid="admin-new-post" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-head font-bold text-white transition-transform hover:-translate-y-0.5" style={{ background: "#F97316" }}>
                <Plus size={16} /> New Post
              </button>
            </div>
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4" data-testid={`admin-post-${p.slug}`}>
                  <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-head font-bold text-slate-900 truncate">{p.title}</div>
                    <div className="text-sm text-slate-500">{p.category} · {p.read_time}</div>
                  </div>
                  <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.slug}`} className="w-9 h-9 rounded-lg grid place-items-center text-slate-600 hover:bg-slate-100"><Pencil size={16} /></button>
                  <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.slug}`} className="w-9 h-9 rounded-lg grid place-items-center text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="admin-leads">
            <LeadColumn title="Contact Enquiries" icon={Mail} items={leads.contacts} render={(c) => (
              <><div className="font-bold text-slate-900">{c.name}</div><div className="text-sm text-slate-500">{c.email} · {c.phone}</div>{c.service && <div className="text-xs text-[var(--dm-purple)] mt-1">{c.service}</div>}<p className="text-sm text-slate-600 mt-2">{c.message}</p></>
            )} />
            <LeadColumn title="Consultation Requests" icon={Phone} items={leads.consultations} render={(c) => (
              <><div className="font-bold text-slate-900">{c.name}</div><div className="text-sm text-slate-500">{c.email} · {c.phone}</div>{c.business && <div className="text-xs text-[var(--dm-purple)] mt-1">{c.business}</div>}{c.goals && <p className="text-sm text-slate-600 mt-2">{c.goals}</p>}</>
            )} />
            <LeadColumn title="Newsletter Subscribers" icon={Users} items={leads.subscribers} render={(s) => (
              <div className="font-medium text-slate-800">{s.email}</div>
            )} />
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="admin-post-editor">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-head font-extrabold text-xl text-slate-900">{editing === "new" ? "New Post" : "Edit Post"}</h3>
              <button onClick={() => setEditing(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <input required value={form.title} onChange={set("title")} placeholder="Title" data-testid="post-title" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)]" />
              <div className="grid grid-cols-2 gap-4">
                <input value={form.category} onChange={set("category")} placeholder="Category" data-testid="post-category" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)]" />
                <input value={form.author} onChange={set("author")} placeholder="Author" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)]" />
              </div>
              <input value={form.cover_image} onChange={set("cover_image")} placeholder="Cover image URL" data-testid="post-cover" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-purple)]" />
              <textarea required value={form.excerpt} onChange={set("excerpt")} placeholder="Short excerpt" rows={2} data-testid="post-excerpt" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:outline-none focus:border-[var(--dm-purple)]" />
              <textarea required value={form.content} onChange={set("content")} placeholder="Content (use '## ' for headings)" rows={8} data-testid="post-content" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:outline-none focus:border-[var(--dm-purple)]" />
              <button type="submit" disabled={saving} data-testid="post-save" className="w-full rounded-full py-3.5 font-head font-bold text-white flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-70" style={{ background: "linear-gradient(135deg,#6C2BD9,#2563EB)" }}>
                {saving ? <><Loader2 size={18} className="animate-spin" /> Saving…</> : "Save Post"}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

function LeadColumn({ title, icon: Icon, items, render }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-[var(--dm-purple)]" />
        <h3 className="font-head font-bold text-slate-900">{title}</h3>
        <span className="ml-auto text-xs font-bold rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">{items.length}</span>
      </div>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {items.length === 0 ? <p className="text-sm text-slate-400">Nothing yet.</p> : items.map((it, i) => (
          <div key={i} className="rounded-xl bg-[var(--dm-alt)] p-3.5 border border-slate-100">{render(it)}</div>
        ))}
      </div>
    </div>
  );
}
